import { createTheme, ThemeOptions } from '@mui/material/styles'
import { CSSProperties } from '@mui/styles'
import merge from 'lodash/merge'

import { breakpoints } from '@components/layout/breakpoints.css'
import { palette } from '@components/layout/colors/palette'
import { screens } from '@components/layout/screens.css'
import { zIndex } from '@components/layout/zIndex.css'

declare module '@mui/styles' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface, @typescript-eslint/no-empty-object-type
  interface DefaultTheme extends Theme {}
}

const baseThemeOptions = {
  palette,
  zIndex,
  background: '#eef1f4',
  shape: {
    boxShadow: '0 0 2px #00ade2',
    boxShadowGray: '0 0 3px #ccc',
    boxShadowError: '0 0 2px #f00e0e',
  },
  layout: {
    wrapper: {
      boxSizing: 'border-box',
      fontFamily: 'var(--font-swimply), sans-serif',
      '& *': {
        boxSizing: 'border-box',
      },
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: `${breakpoints.max}px`,
      margin: '0 auto',
      width: '100%',
      padding: '10px 15px',
      boxSizing: 'border-box',
      fontFamily: 'var(--font-swimply), sans-serif',
      '& *': {
        boxSizing: 'border-box',
      },
      '@media (max-width: 400px)': {
        padding: '40px 0',
      },
    },
  } as Record<'wrapper' | 'container', CSSProperties>,
  screens,
  breakpoints,
}

const muiThemeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: '#00ade2',
    },
    success: {
      main: palette.brand.tennisGreen,
    },
    common: {
      black: '#222222',
      white: '#ffffff',
    },
  },
  typography: {
    fontFamily: 'var(--font-swimply), sans-serif',
    fontSize: 14,
    body1: {
      color: '#7b858b',
      fontWeight: 'normal',
      fontSize: '14px',
    },
    h1: {
      fontFamily: 'var(--font-swimply)',
      fontSize: '35px',
      fontWeight: 500,
      lineHeight: '1.1',
      color: '#232325',
      margin: 0,
    },
    h2: {
      fontFamily: 'var(--font-swimply)',
      fontSize: '30px',
      fontWeight: 500,
      lineHeight: '1.1',
      color: '#232325',
      margin: 0,
      '@media (max-width:767px)': {
        fontSize: 25,
      },
    },
    h3: {
      fontFamily: 'var(--font-swimply)',
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: '1.1',
      color: '#232325',
      margin: '10px 0',
    },
    h4: {
      fontFamily: 'var(--font-swimply)',
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '1.1',
      color: '#232325',
    },
    h5: {
      fontFamily: 'var(--font-swimply), sans-serif',
      fontSize: '18px',
      fontWeight: 500,
      lineHeight: '1.1',
      color: '#232325',
    },
    h6: {
      fontSize: 'inherit',
      fontWeight: 'inherit',
      lineHeight: 'inherit',
    },
    button: {
      fontFamily: 'var(--font-swimply), sans-serif',
      background: '#22BFEA',
      color: '#fff',
      padding: '10px 25px',
      textAlign: 'center',
      cursor: 'pointer',
      borderRadius: '8px',
      textTransform: 'unset',
      '&:disabled': {
        opacity: 0.5,
      },
      '& span': {
        color: '#fff',
        background: 'transparent',
      },
      '&:hover': {
        color: '#fff',
        background:
          'linear-gradient(0deg, rgba(34, 191, 234, 0.2), rgba(34, 191, 234, 0.2)), rgba(34, 191, 234, 0.2);',
      },
    },
    subtitle1: {
      fontFamily: 'var(--font-swimply), sans-serif',
      fontSize: '14px',
      color: '#7b858b',
    },
    subtitle2: {
      fontFamily: 'var(--font-swimply), sans-serif',
      color: '#232323',
      textTransform: 'uppercase',
      fontSize: '13px',
    },
    caption: {
      fontSize: '14px',
      textAlign: 'center',
      background: '#ffefef',
      padding: '10px',
      color: '#ff0606',
      marginBottom: '10px',
      maxWidth: '351px',
      fontFamily: 'var(--font-swimply), sans-serif',
    },
    overline: {
      fontSize: '14px',
      textAlign: 'center',
      background: '#bdffbd',
      padding: '15px',
      color: '#232323',
      marginBottom: '10px',
      borderRadius: '8px',
      lineHeight: '20px',
      fontFamily: 'var(--font-swimply), sans-serif',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'var(--font-swimply), sans-serif',
          '&:hover': {
            backgroundColor: '#B0E8F7',
          },
          '&.Mui-disabled': {
            color: '#ffffff',
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          fontFamily: 'var(--font-swimply), sans-serif',
        },
      },
    },
  },
}

const muiTheme = createTheme(muiThemeOptions)

const theme = merge({}, muiTheme, baseThemeOptions)
export type Theme = typeof theme

export default theme
